/**
 * @name PersonalData
 * @description Componente de configuración de datos personales del usuario en configuración de perfil
 * @author Ricardo Guerra
 */

import React, { PureComponent } from 'react';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { trackPromise } from 'react-promise-tracker';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { LoadingWrapper } from '@components';
import { AuthService, EmployeeService, ResponseHandlerService, } from '@services';
import { Avatar, Icons, MUI, Typography } from '@styles';

class PersonalData extends PureComponent {
  constructor(props) {
    super(props);
    const infoUsuario = JSON.parse(localStorage.getItem('ls.infoUsuario') || '{}');
    this.userInfo = infoUsuario && infoUsuario.Empleado ? infoUsuario.Empleado : null;
    if (!this.userInfo) {
      AuthService.redirectToLogin();
      return;
    }
    const langObj = JSON.parse(localStorage.getItem('ls.ty-Language') || '{}');
    this.language = langObj && langObj.language ? langObj.language : 'es';
    this.isAdminView = props.isAdminView;
    this.state = {
      photo: '',
      user: '',
      name: '',
      birthDate: '',
      curp: '',
      civilStatus: '',
      academicLevel: '',
      employeeCode: '',
      job: '',
      area: '',
      branch: '',
      startDate: '',
      supervisor: '',
      email: '',
      phone: this.userInfo.Telefono,
      language: this.language
    };

    this.schema = Yup.object({
      phone: Yup.string()
        .min(8, i18n.t('FormsGeneralTexts.min8Chars'))
        .matches(/^[0-9]*$/, i18n.t('FormsGeneralTexts.onlyDigits'))
    });
  }

  componentDidMount() {
    this.getUserInfo();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.filters !== this.props.filters) {
      this.getUserInfo();
    }
  }

  getEmployeeId = () => {
    let employee = this.props.filters ? this.props.filters.employee : null;
    return this.isAdminView && employee ? employee.IdEmpleado : this.userInfo.IdEmpleado;
  }

  getUserInfo = () => {
    let employeeId = this.getEmployeeId();

    trackPromise(
      EmployeeService.getOne(employeeId)
        .then((response) => {
          this.setState({
            photo: response.Foto || 'https://assets.talentry.io/icons/profile/TAL_Avatar.png',
            user: response.Usuario,
            name: response.Nombre,
            birthDate: response.FechaNacimiento,
            curp: response.Curp,
            civilStatus: response.EstadoCivil,
            academicLevel: response.NivelAcademico,
            employeeCode: response.CodigoEmpleado,
            job: response.Puesto,
            area: response.Area,
            branch: response.Sucursal,
            startDate: response.FechaInicio,
            supervisor: response.NombreFacilitador,
            email: response.Email,
            phone: response.Telefono
          });
        })
        .catch(() => {
          ResponseHandlerService.error();
        }),
      'loading-tab-personal-data'
    );
  }

  getEmployeeId = () => {
    let employee = this.props.filters ? this.props.filters.employee : null;
    return this.isAdminView && employee ? employee.IdEmpleado : this.userInfo.IdEmpleado;
  }

  saveUserInfo = (values, setSubmitting) => {
    const { language } = this.state;

    let employee = this.props.filters ? this.props.filters.employee : null;
    let userId = this.isAdminView && employee ? employee.IdUsuario : this.userInfo.IdUsuario;

    let request = {
      ...values,
      IdUsuario: userId
    };

    EmployeeService.putProfile(request)
      .then((response) => {
        // Switch Language
        localStorage.setItem('ls.ty-Language', JSON.stringify({ language: values.language }));
        i18n.use(initReactI18next).init({ lng: values.language });

        EmployeeService.getInfo()
          .then((response) => {
            this.getUserInfo();
            localStorage.removeItem('ls.infoUsuario');
            localStorage.setItem('ls.infoUsuario', JSON.stringify(response));
            setSubmitting(false);
          });

        ResponseHandlerService.success(response);
        if (values.language !== language) {
          window.location.reload();
        }
      })
      .catch((error) => {
        setSubmitting(false);
        ResponseHandlerService.error(error);
      });
  }

  render() {
    const {
      photo,
      user,
      name,
      birthDate,
      curp,
      civilStatus,
      academicLevel,
      employeeCode,
      job,
      area,
      branch,
      startDate,
      supervisor,
      email,
      phone,
      language
    } = this.state;

    return (
      <LoadingWrapper area="loading-tab-personal-data">
        <Styled.FlexContainer>
          {/* Flex 20% */}
          <Styled.Flex20>
            {/* Avatar y Nombre */}
            <Styled.FlexCol>
              <MUI.Box padding="8px">
                <Avatar size="xl" src={photo} shadow />
              </MUI.Box>
              <Styled.BoxName>
                <Styled.Label>{i18n.t('GenericMessages.name')}</Styled.Label>
                <Typography.Text>{name}</Typography.Text>
              </Styled.BoxName>
            </Styled.FlexCol>
          </Styled.Flex20>
          {/* Flex 80% */}
          <Styled.Flex80>
            <MUI.Grid container>
              <Styled.FlexRow>
                <MUI.Grid container direction="row" item>
                  {/* Usuario */}
                  <MUI.Grid item sm={3} xs={12}>
                    <Styled.BoxData>
                      <Styled.Label>{i18n.t('GenericMessages.user')}</Styled.Label>
                      <Typography.Text>{user}</Typography.Text>
                    </Styled.BoxData>
                  </MUI.Grid>
                  {/* CURP */}
                  <MUI.Grid item sm={3} xs={12}>
                    <Styled.BoxData>
                      <Styled.Label>{i18n.t('CURP')}</Styled.Label>
                      <Typography.Text>{curp ? curp : '-'}</Typography.Text>
                    </Styled.BoxData>
                  </MUI.Grid>
                  {/* Estatus Civil */}
                  <MUI.Grid item sm={3} xs={12}>
                    <Styled.BoxData>
                      <Styled.Label>{i18n.t('GenericMessages.civilStatus')}</Styled.Label>
                      <Typography.Text>{civilStatus ? civilStatus : '-'}</Typography.Text>
                    </Styled.BoxData>
                  </MUI.Grid>
                  {/* Nivel Académico */}
                  <MUI.Grid item sm={3} xs={12}>
                    <Styled.BoxData>
                      <Styled.Label>{i18n.t('GenericMessages.academicLevel')}</Styled.Label>
                      <Typography.Text>{academicLevel ? academicLevel : '-'}</Typography.Text>
                    </Styled.BoxData>
                  </MUI.Grid>
                </MUI.Grid>
              </Styled.FlexRow>

              <Styled.FlexRow>
                <MUI.Grid container direction="row" item>
                  {/* Código Empleado */}
                  <MUI.Grid item sm={3} xs={12}>
                    <Styled.BoxData>
                      <Styled.Label>{i18n.t('GenericMessages.employeeCode')}</Styled.Label>
                      <Typography.Text>{employeeCode}</Typography.Text>
                    </Styled.BoxData>
                  </MUI.Grid>
                  {/* Puesto */}
                  <MUI.Grid item sm={3} xs={12}>
                    <Styled.BoxData>
                      <Styled.Label>{i18n.t('GenericMessages.job')}</Styled.Label>
                      <Typography.Text>{job}</Typography.Text>
                    </Styled.BoxData>
                  </MUI.Grid>
                  {/* Área */}
                  <MUI.Grid item sm={3} xs={12}>
                    <Styled.BoxData>
                      <Styled.Label>{i18n.t('GenericMessages.area')}</Styled.Label>
                      <Typography.Text>{area}</Typography.Text>
                    </Styled.BoxData>
                  </MUI.Grid>
                  {/* Sucursal */}
                  <MUI.Grid item sm={3} xs={12}>
                    <Styled.BoxData>
                      <Styled.Label>{i18n.t('GenericMessages.branch')}</Styled.Label>
                      <Typography.Text>{branch}</Typography.Text>
                    </Styled.BoxData>
                  </MUI.Grid>
                </MUI.Grid>
              </Styled.FlexRow>

              <Styled.FlexRow>
                <MUI.Grid container direction="row" item>
                  {/* Fecha Nacimiento */}
                  <MUI.Grid item sm={3} xs={12}>
                    <Styled.BoxData>
                      <Styled.Label>{i18n.t('GenericMessages.birthDate')}</Styled.Label>
                      <Typography.Text>{moment(birthDate).format(i18n.t('FormatDates.short'))}</Typography.Text>
                    </Styled.BoxData>
                  </MUI.Grid>
                  {/* Fecha Inicio */}
                  <MUI.Grid item sm={3} xs={12}>
                    <Styled.BoxData>
                      <Styled.Label>{i18n.t('GenericMessages.startDate')}</Styled.Label>
                      <Typography.Text>{moment(startDate).format(i18n.t('FormatDates.short'))}</Typography.Text>
                    </Styled.BoxData>
                  </MUI.Grid>
                  {/* Supervisor */}
                  <MUI.Grid item sm={3} xs={12}>
                    <Styled.BoxData>
                      <Styled.Label>{i18n.t('GenericMessages.supervisor')}</Styled.Label>
                      <Typography.Text>{supervisor ? supervisor : '-'}</Typography.Text>
                    </Styled.BoxData>
                  </MUI.Grid>
                  {/* Email */}
                  <MUI.Grid item sm={3} xs={12}>
                    <Styled.BoxData>
                      <Styled.Label>{i18n.t('GenericMessages.email')}</Styled.Label>
                      <Typography.Text>{email}</Typography.Text>
                    </Styled.BoxData>
                  </MUI.Grid>
                </MUI.Grid>
              </Styled.FlexRow>

              <Formik
                validateOnMount
                initialValues={{ phone, language }}
                initialErrors={{ phone, language }}
                initialTouched={{ phone, language }}
                validationSchema={this.schema}
                onSubmit={(values, { setSubmitting }) => {
                  this.saveUserInfo(values, setSubmitting);
                }}
              >
                {(formikProps) => {
                  return (
                    <>
                      <Styled.FlexRow>
                        <MUI.Grid container direction="row" item>
                          {/* Teléfono */}
                          <MUI.Grid item sm xs={12}>
                            <MUI.Box padding="8px">
                              <MUI.TextFieldIcon
                                label={i18n.t('GenericMessages.phone')}
                                type="text"
                                name="phone"
                                value={formikProps.values.phone}
                                onBlur={formikProps.handleBlur}
                                onChange={formikProps.handleChange}
                                error={Boolean(formikProps.touched.phone && formikProps.errors.phone)}
                                helperText={formikProps.touched.phone && formikProps.errors.phone}
                                InputProps={{ startAdornment: (<Icons.Phone />) }}
                              />
                            </MUI.Box>
                          </MUI.Grid>
                          {/* Idioma */}
                          <MUI.Grid item sm xs={12}>
                            <MUI.Box padding="8px">
                              <MUI.TextFieldIcon
                                select
                                label={i18n.t('Topbar.language')}
                                type="text"
                                name="language"
                                value={formikProps.values.language}
                                onChange={formikProps.handleChange}
                                InputProps={{ startAdornment: (<Icons.Translate />) }}
                              >
                                <MUI.SelectOption key="es" value="es">
                                  {i18n.t('GenericMessages.es')}
                                </MUI.SelectOption>
                                <MUI.SelectOption key="en" value="en">
                                  {i18n.t('GenericMessages.en')}
                                </MUI.SelectOption>
                                <MUI.SelectOption key="pt" value="pt">
                                  {i18n.t('GenericMessages.pt')}
                                </MUI.SelectOption>
                              </MUI.TextFieldIcon>
                            </MUI.Box>
                          </MUI.Grid>
                          <MUI.Grid item sm></MUI.Grid>
                          <MUI.Grid item sm></MUI.Grid>
                        </MUI.Grid>
                      </Styled.FlexRow>

                      <Styled.FlexActions>
                        <MUI.ButtonCTA
                          type="button"
                          color="primary"
                          disabled={!formikProps.isValid || formikProps.isSubmitting}
                          onClick={formikProps.handleSubmit}
                        >
                          {i18n.t('DialogConfirm.save')}
                        </MUI.ButtonCTA>
                      </Styled.FlexActions>
                    </>
                  );
                }}
              </Formik>
            </MUI.Grid>
          </Styled.Flex80>
        </Styled.FlexContainer>
      </LoadingWrapper>
    );
  }
}

PersonalData.propTypes = {
  // filtro para administrador.
  filters: PropTypes.object,
  // Es vista adminitrador
  isAdminView: PropTypes.bool
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.FlexContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

Styled.Flex20 = styled.div`
  display: flex;
  flex: 20%;
`;

Styled.Flex80 = styled.div`
  display: flex;
  flex: 80%;
`;

Styled.FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  padding: 8px;
`;

Styled.FlexCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 8px;
`;

Styled.FlexActions = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  padding: 8px;
`;

Styled.BoxName = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 8px;
`;

Styled.BoxData = styled.div`
  width: 100%;
  padding: 8px;
`;

Styled.Label = styled.p`
  font-family: 'Bryant Pro Medium';
  font-size: ${(props) => props.theme.typography.smallText};
  line-height: inherit;
`;

export default PersonalData;
